import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import { Search, Film as FilmIcon, Info, CreditCard, XCircle, X, Loader2, AlertTriangle, RefreshCw } from 'lucide-react'

type Row = {
  id: number
  screening_id: number
  seats: number
  total_amount: number
  status: string
  payment_intent_id?: string | null
  payment_status?: string | null
  capture_scheduled_at?: string | null
  captured_at?: string | null
  refund_id?: string | null
  refund_amount?: number | null
  convenience_fee?: number | null
  customer_name?: string | null
  phonebooking?: boolean | null
  created_at?: string | null
  phonenumber?: string | null
  booking_seats?: { seat_ids: string[] }[]
  profiles?: { email: string }[] | null
  user_id?: string | null
  // ⚠️ Populated after cross-checking with Stripe metadata
  stripe_amount_eur?: number | null
  price_mismatch?: boolean
}

type ScreeningRow = { id: number; movie_id: number; start_time: string }
type MovieRow = { id: number; title: string }

export default function OrganizerBookings({ organizerId }: { organizerId: number | null }) {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<Row[]>([])
  const [screenings, setScreenings] = useState<ScreeningRow[]>([])
  const [movies, setMovies] = useState<MovieRow[]>([])
  const [selectedScreening, setSelectedScreening] = useState<number | ''>('')
  const [bookingIdQuery, setBookingIdQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'intent' | 'hold' | 'captured' | 'cancelled'>('all')
  const [commissionPercent, setCommissionPercent] = useState<number>(0)
  const [refundingMismatch, setRefundingMismatch] = useState(false)

  useEffect(() => {
    (async () => {
      if (!organizerId) return
      setLoading(true)
      try {
        const { data: org } = await supabase.from('organizers').select('commission_percent').eq('id', organizerId).single()
        setCommissionPercent(org?.commission_percent || 0)
        const { data: ms } = await supabase.from('movies').select('id,title').eq('organizer_id', organizerId)
        setMovies(((ms || []) as MovieRow[]))
        const ids = ((ms || []) as MovieRow[]).map(m => m.id)
        const { data: ss } = await supabase.from('screenings').select('id,movie_id,start_time').in('movie_id', ids)
        setScreenings(((ss || []) as ScreeningRow[]))
        const sids = ((ss || []) as ScreeningRow[]).map(s => s.id)
        if (sids.length) {
          const { data: bs } = await supabase.from('bookings').select('id,screening_id,seats,total_amount,status,payment_intent_id,payment_status,capture_scheduled_at,captured_at,refund_id,refund_amount,convenience_fee,customer_name,phonebooking,phonenumber,created_at,booking_seats(seat_ids),user_id').in('screening_id', sids)

          let rowsWithProfiles = (bs || []) as Row[];

          if (rowsWithProfiles.length > 0) {
            // @ts-ignore
            const userIds = [...new Set(rowsWithProfiles.map(r => r.user_id).filter(Boolean))];
            if (userIds.length > 0) {
              const { data: profiles } = await supabase.from('profiles').select('id, email').in('id', userIds);
              const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

              rowsWithProfiles = rowsWithProfiles.map(row => ({
                ...row,
                // @ts-ignore
                profiles: profileMap.has(row.user_id) ? [{ email: profileMap.get(row.user_id)?.email }] : []
              }));
            }

            // ⚠️ Price Mismatch Detection
            // For bookings that only have a payment intent (not yet captured/succeeded),
            // cross-check the Stripe intent amount via our edge function.
            // We do this for a batch of suspect bookings (intent status, not phone, not cancelled).
            const suspectBookings = rowsWithProfiles.filter(
              r => r.payment_intent_id &&
                   r.payment_status !== 'succeeded' &&
                   r.payment_status !== 'phone_booking' &&
                   r.status !== 'cancelled'
            );
            if (suspectBookings.length > 0) {
              try {
                const { data: verifyData } = await supabase.functions.invoke<{
                  results: { booking_id: number; stripe_amount_eur: number; db_amount: number; mismatch: boolean }[]
                }>('verify-payment-amounts', {
                  body: { booking_ids: suspectBookings.map(b => b.id) }
                });
                if (verifyData?.results) {
                  const mismatchMap = new Map(
                    verifyData.results.map(r => [r.booking_id, { stripe_amount_eur: r.stripe_amount_eur, mismatch: r.mismatch }])
                  );
                  rowsWithProfiles = rowsWithProfiles.map(row => {
                    const check = mismatchMap.get(row.id);
                    if (check) return { ...row, stripe_amount_eur: check.stripe_amount_eur, price_mismatch: check.mismatch };
                    return row;
                  });
                }
              } catch {
                // Edge function may not exist yet — silently skip mismatch detection
              }
            }
          }

          setRows(rowsWithProfiles)
        } else setRows([])
      } catch (e) {
        toast.error('Failed to load bookings')
      }
      setLoading(false)
    })()
  }, [organizerId])

  const screeningMap = useMemo(() => {
    const map: Record<number, ScreeningRow> = {}
    for (const s of screenings) map[s.id] = s
    return map
  }, [screenings])
  const movieMap = useMemo(() => {
    const map: Record<number, MovieRow> = {}
    for (const m of movies) map[m.id] = m
    return map
  }, [movies])

  const [bookingType, setBookingType] = useState<'all' | 'phone' | 'online'>('all')
  const [showDownload, setShowDownload] = useState(false)
  const [dlScreening, setDlScreening] = useState<number | ''>('')
  const [dlStatus, setDlStatus] = useState<'all' | 'intent' | 'hold' | 'captured' | 'cancelled'>('all')
  const [dlType, setDlType] = useState<'all' | 'phone' | 'online'>('all')

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (selectedScreening && r.screening_id !== Number(selectedScreening)) return false
      if (bookingIdQuery && !String(r.id).includes(bookingIdQuery.trim())) return false
      if (statusFilter === 'intent' && !r.payment_intent_id) return false
      if (statusFilter === 'hold' && r.payment_status !== 'requires_capture') return false
      if (statusFilter === 'captured' && r.payment_status !== 'succeeded') return false
      if (statusFilter === 'cancelled' && (r.status || '') !== 'cancelled') return false
      if (bookingType === 'phone' && !r.phonebooking) return false
      if (bookingType === 'online' && !!r.phonebooking) return false
      return true
    })
  }, [rows, selectedScreening, bookingIdQuery, statusFilter, bookingType])

  const capturePayment = async (bookingId: number) => {
    try {
      const row = rows.find(r => r.id === bookingId)
      if (!row || row.payment_status !== 'requires_capture') return
      const { data, error } = await supabase.functions.invoke('capture-payment-intent', { body: { booking_id: bookingId } })
      if (error) { toast.error('Capture failed'); return }
      toast.success('Payment captured')
      const capturedAt = new Date().toISOString()
      setRows(prev => prev.map(r => r.id === bookingId ? { ...r, payment_status: 'succeeded', captured_at: capturedAt } : r))
      setActiveRow(ar => (ar && ar.id === bookingId) ? { ...ar, payment_status: 'succeeded', captured_at: capturedAt } : ar)
    } catch {
      toast.error('Capture failed')
    }
  }

  /**
   * Cancel the Stripe payment intent for a price-mismatched booking,
   * mark it cancelled in our DB, and optionally send a notification to the customer.
   * Since the intent was never captured, the customer is NOT charged.
   */
  const cancelMismatchedBooking = async (bookingId: number) => {
    const row = rows.find(r => r.id === bookingId)
    if (!row) return

    const customerEmail = row.profiles?.[0]?.email
    const customerName  = row.customer_name || 'Customer'
    const stripeAmt     = row.stripe_amount_eur?.toFixed(2) ?? '?'
    const dbAmt         = Number(row.total_amount || 0).toFixed(2)

    const confirmed = window.confirm(
      `⚠️ Price Mismatch Detected\n\n` +
      `Booking #${bookingId} was recorded as €${dbAmt} in the system, ` +
      `but the Stripe intent was only created for €${stripeAmt}.\n\n` +
      `This will:\n` +
      `• Cancel the Stripe payment intent (customer is NOT charged)\n` +
      `• Mark the booking as cancelled in the system\n` +
      `• The customer (${customerName}${customerEmail ? ', ' + customerEmail : ''}) ` +
      `should be contacted to re-book at the correct price.\n\n` +
      `Proceed?`
    )
    if (!confirmed) return

    try {
      setRefundingMismatch(true)
      // Use existing cancel-booking edge function which voids the intent
      const { data: cancelData, error } = await supabase.functions.invoke('cancel-booking', {
        body: {
          booking_id:        bookingId,
          fee_percent:       0,              // No cancellation fee — this is our error
          reason:            'price_mismatch',
          stripe_amount_eur: row.stripe_amount_eur ?? undefined,  // passed to email template
        }
      })
      if (error) {
        toast.error('Failed to cancel mismatched booking. Try the standard Cancel button.')
        return
      }
      setRows(prev => prev.map(r =>
        r.id === bookingId ? { ...r, status: 'cancelled', price_mismatch: false } : r
      ))
      setActiveRow(ar =>
        (ar && ar.id === bookingId) ? { ...ar, status: 'cancelled', price_mismatch: false } : ar
      )

      // Check warnings from the edge function
      const seatWarning  = (cancelData as any)?.seat_cleanup_warning
      const emailWarning = (cancelData as any)?.email_warning

      if (seatWarning) {
        toast.error(
          `⚠️ Booking cancelled but seat cleanup had an issue: ${seatWarning}. ` +
          `The seats may still appear locked — check Supabase booking_seats table for booking #${bookingId}.`,
          { duration: 12000 }
        )
      } else {
        const emailNote = emailWarning
          ? `Email could not be sent (${emailWarning}). Please contact the customer manually.`
          : customerEmail
            ? `📧 Notification email sent to ${customerEmail}.`
            : `No email found — please contact the customer directly.`
        toast.success(
          `✅ Booking #${bookingId} cancelled & seats freed. ${emailNote}`,
          { duration: 10000 }
        )
      }
    } catch {
      toast.error('Unexpected error during mismatch cancellation')
    } finally {
      setRefundingMismatch(false)
    }
  }

  const cancelBooking = async (bookingId: number) => {
    try {
      const { data, error } = await supabase.functions.invoke('cancel-booking', { body: { booking_id: bookingId, fee_percent: 8 } })

      // Fallback: Check if payment was already cancelled in Stripe
      if (error && (error.context?.json?.error === 'stripe_payment_already_cancelled' || (await error.context?.json())?.error === 'stripe_payment_already_cancelled')) {
        if (window.confirm('Payment Intent was already cancelled in Stripe. Mark booking as cancelled in system?')) {
          const { error: retryError } = await supabase.functions.invoke('cancel-booking', { body: { booking_id: bookingId, fee_percent: 8, force_sync: true } })
          if (retryError) {
            toast.error('Force cancel failed')
            return
          }
          toast.success('Booking force-cancelled')
          setRows(prev => prev.map(r => r.id === bookingId ? { ...r, status: 'cancelled' } : r))
          setActiveRow(ar => (ar && ar.id === bookingId) ? { ...ar, status: 'cancelled' } : ar)
          return
        }
      }

      if (error) { toast.error('Cancel failed'); return }
      toast.success('Booking cancelled')
      // optimistic update
      setRows(prev => prev.map(r => r.id === bookingId ? { ...r, status: 'cancelled' } : r))
      setActiveRow(ar => (ar && ar.id === bookingId) ? { ...ar, status: 'cancelled' } : ar)
    } catch {
      toast.error('Cancel failed')
    }
  }

  const [showDetails, setShowDetails] = useState(false)
  const [activeRow, setActiveRow] = useState<Row | null>(null)

  const openDetails = (row: Row) => { setActiveRow(row); setShowDetails(true) }
  const closeDetails = () => { setShowDetails(false); setActiveRow(null) }
  const [processingCapture, setProcessingCapture] = useState(false)
  const [processingCancel, setProcessingCancel] = useState(false)

  // Use a derived variable for active movie/screening details
  const activeDetails = useMemo(() => {
    if (!activeRow) return null
    const s = screeningMap[activeRow.screening_id]
    const m = s ? movieMap[s.movie_id] : undefined
    return {
      movieTitle: m?.title || 'Unknown Movie',
      screeningTime: s ? new Date(s.start_time).toLocaleString() : 'Unknown Time',
      seatIds: activeRow.booking_seats?.[0]?.seat_ids?.join(', ') || '—'
    }
  }, [activeRow, screeningMap, movieMap])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-gray-400">
            <Search className="h-4 w-4" />
          </div>
          <input
            value={bookingIdQuery}
            onChange={e => setBookingIdQuery(e.target.value)}
            className="h-10 pl-9 pr-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm"
            placeholder="Search by Booking ID"
          />
        </label>
        <select
          value={selectedScreening}
          onChange={e => setSelectedScreening(e.target.value ? Number(e.target.value) : '')}
          className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
        >
          <option value="">All Screenings</option>
          {screenings.map(s => (
            <option key={s.id} value={s.id}>
              {new Date(s.start_time).toLocaleString()} — {(movieMap[s.movie_id]?.title) || 'Untitled'}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as any)}
          className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
        >
          <option value="all">All Payments</option>
          <option value="intent">Payment Intent Created</option>
          <option value="hold">Payment Hold (requires_capture)</option>
          <option value="captured">Payment Captured</option>
          <option value="cancelled">Payments Cancelled</option>
        </select>
        <select
          value={bookingType}
          onChange={e => setBookingType(e.target.value as any)}
          className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
        >
          <option value="all">All Types</option>
          <option value="phone">Phone Booking (paid to bank)</option>
          <option value="online">Online Card</option>
        </select>
        <button
          onClick={() => { setShowDownload(true); setDlScreening(selectedScreening); setDlStatus(statusFilter); setDlType(bookingType) }}
          className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm"
        >
          Download Bookings
        </button>
      </div>

      <div className="w-full overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-[1000px] w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Booking</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Movie</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Payment Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Details</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr><td className="px-4 py-6 text-gray-500 dark:text-gray-400" colSpan={5}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td className="px-4 py-6 text-gray-500 dark:text-gray-400" colSpan={5}>No bookings found</td></tr>
            ) : (
              filtered.map(r => {
                const s = screeningMap[r.screening_id]
                const m = s ? movieMap[s.movie_id] : undefined
                const hasMismatch = !!r.price_mismatch && r.status !== 'cancelled'
                return (
                  <tr key={r.id} className={hasMismatch ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        #{r.id}
                        {hasMismatch && (
                          <span title="Price mismatch — Stripe amount differs from system amount">
                            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap flex items-center gap-2"><FilmIcon className="h-4 w-4" />{m?.title || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(r.status || '') === 'cancelled' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-semibold">Cancelled</span>
                        ) : (r.payment_status === 'succeeded') ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-semibold">Captured</span>
                        ) : (r.payment_status === 'requires_capture') ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-100 text-yellow-700 text-xs font-semibold">Hold</span>
                        ) : (r.payment_status === 'phone_booking') ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-semibold">Phone</span>
                        ) : r.payment_intent_id ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-100 text-indigo-700 text-xs font-semibold">Intent</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-semibold">—</span>
                        )}
                        {hasMismatch && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-semibold">
                            <AlertTriangle className="h-3 w-3" />
                            Price Mismatch
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <button onClick={() => openDetails(r)} className="flex items-center gap-2 px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white">
                        <Info className="h-4 w-4" />
                        <span className="hidden sm:inline">Details</span>
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {showDetails && activeRow && activeDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Booking #{activeRow.id}</h3>
              <button onClick={closeDetails} className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white flex items-center gap-2"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-4 space-y-4">

              {/* ⚠️ Price Mismatch Alert Banner */}
              {activeRow.price_mismatch && activeRow.status !== 'cancelled' && (
                <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-800 dark:text-red-300 text-sm">Price Mismatch Detected</p>
                      <p className="text-red-700 dark:text-red-400 text-xs mt-1">
                        The system recorded <strong>€{Number(activeRow.total_amount || 0).toFixed(2)}</strong> for this booking,
                        but the Stripe payment intent was only created for <strong>€{activeRow.stripe_amount_eur?.toFixed(2) ?? '?'}</strong>.
                        This happened because the pricing was read from the wrong event when the intent was created.
                      </p>
                      <p className="text-red-700 dark:text-red-400 text-xs mt-1">
                        <strong>No money has been charged yet</strong> — the intent has not been captured.
                        You should cancel this intent and ask the customer to re-book at the correct price.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => cancelMismatchedBooking(activeRow.id)}
                          disabled={refundingMismatch}
                          className="flex items-center gap-2 px-4 py-2 rounded bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60"
                        >
                          {refundingMismatch
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Cancelling Intent…</>
                            : <><XCircle className="h-4 w-4" /> Cancel Intent &amp; Notify Customer</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Customer</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.customer_name || '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Email</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.profiles?.[0]?.email || '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Phone</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.phonenumber || '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Created At</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.created_at ? new Date(activeRow.created_at).toLocaleString() : '—'}</span>
                </div>
                <div className="flex flex-col col-span-1 sm:col-span-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Movie & Time</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{activeDetails.movieTitle} <span className="text-gray-400">|</span> {activeDetails.screeningTime} </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Payment Status</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.status === 'cancelled' ? 'Cancelled' : (activeRow.payment_status || '—')}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Total — System (€)</span>
                  <span className="text-sm text-gray-900 dark:text-white">€{Number(activeRow.total_amount || 0).toFixed(2)}</span>
                  {activeRow.stripe_amount_eur != null && activeRow.price_mismatch && (
                    <span className="text-xs text-red-600 dark:text-red-400 font-semibold">
                      Stripe intent: €{activeRow.stripe_amount_eur.toFixed(2)}
                      {' '}(−€{(Number(activeRow.total_amount || 0) - activeRow.stripe_amount_eur).toFixed(2)} shortfall)
                    </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Seats ({activeRow.seats})</span>
                  <span className="text-sm text-gray-900 dark:text-white font-mono break-all">{activeDetails.seatIds}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Intent ID</span>
                  <span className="text-xs break-all text-gray-900 dark:text-white">{activeRow.payment_intent_id || '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Capture Scheduled</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.capture_scheduled_at ? new Date(activeRow.capture_scheduled_at).toLocaleString() : '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Captured At</span>
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.captured_at ? new Date(activeRow.captured_at).toLocaleString() : '—'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-900 dark:text-white">{activeRow.convenience_fee ? `Fee €${Number(activeRow.convenience_fee).toFixed(2)}` : ''}{activeRow.refund_amount ? `${activeRow.convenience_fee ? ', ' : ''}Refund €${Number(activeRow.refund_amount).toFixed(2)}` : (!activeRow.convenience_fee ? '—' : '')}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Admin Commission ({commissionPercent}%)</span>
                  <span className="text-sm text-gray-900 dark:text-white">€{(Number(activeRow.total_amount || 0) * (commissionPercent / 100)).toFixed(2)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Net Earnings</span>
                  <span className="text-sm text-green-600 dark:text-green-400 font-semibold">€{(Number(activeRow.total_amount || 0) * (1 - commissionPercent / 100)).toFixed(2)}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {(activeRow.status || '') === 'cancelled' ? (
                  <span className="px-4 py-2 rounded bg-red-100 text-red-700 font-semibold">Cancelled</span>
                ) : (
                  <>
                    <button
                      onClick={async () => {
                        if (processingCapture) return
                        try {
                          setProcessingCapture(true)
                          await capturePayment(activeRow.id)
                        } finally {
                          setProcessingCapture(false)
                        }
                      }}
                      disabled={processingCapture || !((activeRow.payment_status || '') === 'requires_capture')}
                      className={`flex items-center gap-2 px-4 py-2 rounded ${processingCapture ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : (((activeRow.payment_status || '') === 'requires_capture') ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed')}`}
                    >
                      {processingCapture ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                      {processingCapture ? 'Processing…' : 'Capture Payment'}
                    </button>
                    <button
                      onClick={async () => {
                        if (processingCancel) return
                        try {
                          setProcessingCancel(true)
                          await cancelBooking(activeRow.id)
                        } finally {
                          setProcessingCancel(false)
                        }
                      }}
                      disabled={processingCancel}
                      className={`flex items-center gap-2 px-4 py-2 rounded ${processingCancel ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-500/10 text-red-600'}`}
                    >
                      {processingCancel ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                      {processingCancel ? 'Processing…' : 'Cancel Booking'}
                    </button>
                  </>
                )}
                <div className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
                  <span>Payment Status: {(activeRow.status || '') === 'cancelled' ? 'Cancelled' : (activeRow.payment_status || '—')}</span>
                  <span>Captured At: {activeRow.captured_at ? new Date(activeRow.captured_at).toLocaleString() : '—'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDownload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download Bookings</h3>
              <button onClick={() => setShowDownload(false)} className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white flex items-center gap-2"><X className="h-4 w-4" />Close</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={dlScreening}
                  onChange={e => setDlScreening(e.target.value ? Number(e.target.value) : '')}
                  className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                >
                  <option value="">All Screenings</option>
                  {screenings.map(s => (
                    <option key={s.id} value={s.id}>{new Date(s.start_time).toLocaleString()} — {(movieMap[s.movie_id]?.title) || 'Untitled'}</option>
                  ))}
                </select>
                <select
                  value={dlStatus}
                  onChange={e => setDlStatus(e.target.value as any)}
                  className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                >
                  <option value="all">All Payments</option>
                  <option value="intent">Payment Intent Created</option>
                  <option value="hold">Payment Hold (requires_capture)</option>
                  <option value="captured">Payment Captured</option>
                  <option value="cancelled">Payments Cancelled</option>
                </select>
                <select
                  value={dlType}
                  onChange={e => setDlType(e.target.value as any)}
                  className="h-10 min-w-[200px] rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm px-3"
                >
                  <option value="all">All Types</option>
                  <option value="phone">Phone Booking (paid to bank)</option>
                  <option value="online">Online Card</option>
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => {
                    const data = rows.filter(r => {
                      if (dlScreening && r.screening_id !== Number(dlScreening)) return false
                      if (dlStatus === 'intent' && !r.payment_intent_id) return false
                      if (dlStatus === 'hold' && r.payment_status !== 'requires_capture') return false
                      if (dlStatus === 'captured' && r.payment_status !== 'succeeded') return false
                      if (dlStatus === 'cancelled' && (r.status || '') !== 'cancelled') return false
                      if (dlType === 'phone' && !r.phonebooking) return false
                      if (dlType === 'online' && !!r.phonebooking) return false
                      return true
                    }).map(r => {
                      const s = screeningMap[r.screening_id]
                      const m = s ? movieMap[s.movie_id] : undefined
                      const payment_done = (r.payment_status || '') === 'succeeded'
                      return {
                        booking_id: r.id,
                        name: r.customer_name || '',
                        tickets: r.seats,
                        price: Number(r.total_amount || 0).toFixed(2),
                        payment_done,
                        payment_status: r.payment_status || '',
                        payment_intent_id: r.payment_intent_id || '',
                        capture_scheduled_at: r.capture_scheduled_at || '',
                        captured_at: r.captured_at || '',
                        refund_amount: r.refund_amount || 0,
                        convenience_fee: r.convenience_fee || 0,
                        phonebooking: !!r.phonebooking,
                        movie: m?.title || '',
                        screening_time: s ? new Date(s.start_time).toISOString() : ''
                      }
                    })
                    const header = Object.keys(data[0] || { booking_id: '', name: '', tickets: '', price: '', payment_done: '', payment_status: '', payment_intent_id: '', capture_scheduled_at: '', captured_at: '', refund_amount: '', convenience_fee: '', phonebooking: '', movie: '', screening_time: '' })
                    const csvRows = [header.join(','), ...data.map(row => header.map(k => {
                      const v = (row as any)[k]
                      if (typeof v === 'string') {
                        return '"' + v.replace(/"/g, '""') + '"'
                      }
                      return String(v)
                    }).join(','))]
                    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `bookings_${new Date().toISOString()}.csv`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm"
                >
                  Download CSV
                </button>
                <button
                  onClick={async () => {
                    try {
                      const { data, error } = await supabase.functions.invoke<{ pdf_base64: string }>('export-bookings-pdf', {
                        body: {
                          screening_id: dlScreening || null,
                          status: dlStatus,
                          type: dlType
                        }
                      })
                      if (error || !data?.pdf_base64) { toast.error('PDF export failed'); return }
                      const byteChars = atob(data.pdf_base64)
                      const byteNums = new Array(byteChars.length)
                      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i)
                      const byteArray = new Uint8Array(byteNums)
                      const blob = new Blob([byteArray], { type: 'application/pdf' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `bookings_${new Date().toISOString()}.pdf`
                      document.body.appendChild(a)
                      a.click()
                      document.body.removeChild(a)
                      URL.revokeObjectURL(url)
                    } catch {
                      toast.error('PDF export failed')
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
